import {
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from "react";
import { PANEL_BG, PANEL_LINE, TEXT } from "../theme.js";

/**
 * The floating window the Werkbank lives in — its own module because the
 * trader's counter mounts one too. The pane inside is a FIXED-WIDTH layout
 * (`DIALOG_CONTENT_W`), so the window fits it by SCALING, by WIDTH ALONE; the
 * content area scrolls, since a tall pane is a document and the answer is a
 * scrollbar, not a gun at a third of its size.
 */

interface DialogFrame {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface DialogGesture {
  kind: "move" | "resize";
  pointerId: number;
  startX: number;
  startY: number;
  frame: DialogFrame;
}

const DIALOG_MIN_W = 420;
const DIALOG_MIN_H = 300;
const DIALOG_CONTENT_W = 640;
/**
 * The width a vertical scrollbar takes out of the content area. Guessed
 * generously: two pixels short clips the pane.
 */
const SCROLLBAR_W = 12;
/** The padding the content area carries, both sides. */
const DIALOG_PAD = 24;
/** The drag handle's height, from `.moddialog-handle`. */
const DIALOG_HANDLE_H = 34;
/** How much of the host has to be left over around the window when it is first
 *  placed — the same 8 px it is offset by, on both sides. */
const DIALOG_MARGIN = 16;

export function FloatingModDialog(props: {
  title: string;
  rootRef: RefObject<HTMLDivElement | null>;
  /** Rendered in the handle between the title and the ×. A price line, usually. */
  header?: ReactNode;
  onClose: () => void;
  children: (contentScale: number) => ReactNode;
}): ReactElement {
  const [frame, setFrame] = useState<DialogFrame>({ x: 8, y: 8, w: 680, h: 680 });
  const gesture = useRef<DialogGesture | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState(620);

  const { rootRef } = props;
  // 680 × 680 is a wish, not a size: clamped to the host frame on mount and on
  // every window change, against the frame AS IT IS, so a window the player
  // made smaller stays that size.
  useLayoutEffect(() => {
    const fit = (): void => {
      const root = rootRef.current;
      if (root === null) return;
      const maxW = Math.max(DIALOG_MIN_W, root.clientWidth - DIALOG_MARGIN);
      const maxH = Math.max(DIALOG_MIN_H, root.clientHeight - DIALOG_MARGIN);
      setFrame((prev) => {
        const w = Math.min(prev.w, maxW);
        const h = Math.min(prev.h, maxH);
        const x = clamp(prev.x, 0, Math.max(0, root.clientWidth - w));
        const y = clamp(prev.y, 0, Math.max(0, root.clientHeight - h));
        return prev.w === w && prev.h === h && prev.x === x && prev.y === y
          ? prev
          : { x, y, w, h };
      });
    };
    fit();
    window.addEventListener("resize", fit);
    return () => {
      window.removeEventListener("resize", fit);
    };
  }, [rootRef]);

  // Measured UNTRANSFORMED — `scrollHeight` on the scaled element reports the
  // layout height.
  useLayoutEffect(() => {
    const content = contentRef.current;
    if (content === null) return;
    const measure = (): void => {
      setContentHeight(Math.max(1, content.scrollHeight));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  const availableW = Math.max(1, frame.w - DIALOG_PAD - SCROLLBAR_W);
  const contentScale = clamp(availableW / DIALOG_CONTENT_W, 0.5, 1.5);

  const begin = (kind: DialogGesture["kind"], ev: ReactPointerEvent<HTMLDivElement>): void => {
    if (ev.button !== 0) return;
    ev.preventDefault();
    ev.stopPropagation();
    ev.currentTarget.setPointerCapture(ev.pointerId);
    gesture.current = {
      kind,
      pointerId: ev.pointerId,
      startX: ev.clientX,
      startY: ev.clientY,
      frame,
    };
  };

  const move = (ev: ReactPointerEvent<HTMLDivElement>): void => {
    const active = gesture.current;
    if (active === null || active.pointerId !== ev.pointerId) return;
    const bounds = props.rootRef.current?.getBoundingClientRect();
    const root = props.rootRef.current;
    if (bounds === undefined || root === null) return;
    const scaleX = root.clientWidth > 0 ? bounds.width / root.clientWidth : 1;
    const scaleY = root.clientHeight > 0 ? bounds.height / root.clientHeight : 1;
    const dx = (ev.clientX - active.startX) / scaleX;
    const dy = (ev.clientY - active.startY) / scaleY;
    if (active.kind === "move") {
      const maxX = Math.max(0, root.clientWidth - active.frame.w);
      const maxY = Math.max(0, root.clientHeight - DIALOG_HANDLE_H);
      setFrame({
        ...active.frame,
        x: clamp(active.frame.x + dx, 0, maxX),
        y: clamp(active.frame.y + dy, 0, maxY),
      });
      return;
    }
    setFrame({
      ...active.frame,
      w: clamp(
        active.frame.w + dx,
        DIALOG_MIN_W,
        Math.max(DIALOG_MIN_W, root.clientWidth - active.frame.x),
      ),
      h: clamp(
        active.frame.h + dy,
        DIALOG_MIN_H,
        Math.max(DIALOG_MIN_H, root.clientHeight - active.frame.y),
      ),
    });
  };

  const end = (ev: ReactPointerEvent<HTMLDivElement>): void => {
    if (gesture.current?.pointerId !== ev.pointerId) return;
    gesture.current = null;
  };

  return (
    <section
      role="dialog"
      aria-label={props.title}
      style={{
        position: "absolute",
        left: frame.x,
        top: frame.y,
        zIndex: 20,
        width: frame.w,
        height: frame.h,
        minWidth: DIALOG_MIN_W,
        minHeight: DIALOG_MIN_H,
        maxWidth: "100%",
        maxHeight: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        color: TEXT,
        background: PANEL_BG,
        border: `1px solid ${PANEL_LINE}`,
        borderRadius: 3,
        boxShadow: "0 18px 44px rgba(0, 0, 0, 0.82)",
        overflow: "hidden",
      }}
    >
      <div
        className="moddialog-handle"
        onPointerDown={(ev) => begin("move", ev)}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
      >
        <span>{props.title}</span>
        {/* Always a cell, header or not, so the × stays in the third column. */}
        <span>{props.header ?? null}</span>
        <button
          type="button"
          className="invclose"
          title="Close"
          onPointerDown={(ev) => ev.stopPropagation()}
          onClick={props.onClose}
        >
          ×
        </button>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", padding: 12 }}>
        {/* Sized to the TRANSFORMED content: a CSS transform does not affect
            layout, so without this the scroll extent would be the unscaled
            pane's and the window would scroll past the bottom of the drawing. */}
        <div
          style={{
            position: "relative",
            width: DIALOG_CONTENT_W * contentScale,
            height: contentHeight * contentScale,
            margin: "0 auto",
          }}
        >
          <div
            ref={contentRef}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: DIALOG_CONTENT_W,
              transform: `scale(${contentScale})`,
              transformOrigin: "top left",
            }}
          >
            {props.children(contentScale)}
          </div>
        </div>
      </div>
      <div
        className="moddialog-resize"
        title="Resize"
        onPointerDown={(ev) => begin("resize", ev)}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
      />
    </section>
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
